import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonSpinner } from './ButtonSpinner';

import styles from './ButtonShared.module.scss';
import { useMergeRefs } from '@libs/react/ref';
import clsx from 'clsx';
import { ButtonColor } from './ButtonColor';
import { ButtonVariant } from './ButtonVariant';
import { ButtonContext } from './ButtonContext';
import { PlaceholderContext } from '../shared/PlaceholderContext';

type Timeout = ReturnType<typeof setTimeout>;
type Interval = ReturnType<typeof setInterval>;

interface SharedProps {
  busy?: boolean;
  children: ReactNode;
  color?: ButtonColor;
  disabled?: boolean;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  type?: 'button' | 'submit' | 'reset' | undefined;
  variant?: ButtonVariant;
  width?: 'full' | 'auto';
  className?: string;
  onHold?: () => void;
  style?: CSSProperties;
  ref?: Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>;
}

interface AnchorProps extends SharedProps {
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

interface ButtonElementProps extends SharedProps {
  href?: undefined;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export type ButtonSharedProps = AnchorProps | ButtonElementProps;

export function ButtonShared({
  color,
  variant,
  size = 'medium',
  type = 'button',
  onClick: onClickProp,
  onHold,
  style,
  ref,
  ...props
}: ButtonSharedProps) {
  const buttonContext = useContext(ButtonContext);
  const isPlaceholder = useContext(PlaceholderContext);
  const resolvedColor = color ?? buttonContext.color ?? 'default';
  const resolvedVariant = variant ?? buttonContext.variant ?? 'filled';
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const anchorMergedRef = useMergeRefs<HTMLElement>(anchorRef, ref);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonMergedRef = useMergeRefs<HTMLElement>(buttonRef, ref);
  const holdIntrvalRef = useRef<Interval>(null);
  const holdTimeoutlRef = useRef<Timeout>(null);

  useEffect(() => {
    const button = buttonRef.current;

    function startHolding() {
      const timeout = holdTimeoutlRef.current;
      if (!timeout) {
        holdTimeoutlRef.current = setTimeout(() => {
          if (!holdIntrvalRef.current) {
            holdIntrvalRef.current = setInterval(() => {
              onHold?.();
            }, 50);
          }
        }, 500);
      }
    }

    function stopHolding() {
      const timeout = holdTimeoutlRef.current;
      const interval = holdIntrvalRef.current;
      if (timeout) {
        clearTimeout(timeout);
        holdTimeoutlRef.current = null;
        if (interval) {
          clearInterval(interval);
          holdIntrvalRef.current = null;
        }
      }
    }

    if (button && onHold) {
      button.addEventListener('mousedown', startHolding);
      button.addEventListener('mouseup', stopHolding);
      button.addEventListener('mouseleave', stopHolding);
      button.addEventListener('touchstart', stopHolding);
      button.addEventListener('touchend', stopHolding);

      return () => {
        button.removeEventListener('mousedown', startHolding);
        button.removeEventListener('mouseup', stopHolding);
        button.removeEventListener('mouseleave', stopHolding);
        button.removeEventListener('touchstart', stopHolding);
        button.removeEventListener('touchend', stopHolding);
        stopHolding();
      };
    }
  }, [onHold]);

  function onClick(event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    onClickProp?.(
      event as MouseEvent<HTMLAnchorElement> & MouseEvent<HTMLButtonElement>,
    );
  }

  function onClickButton(event: MouseEvent<HTMLButtonElement>) {
    onClick(event);
  }

  function onClickAnchor(event: MouseEvent<HTMLAnchorElement>) {
    if (onClickProp) {
      onClick(event);
    } else {
      event.preventDefault();
      navigate(event.currentTarget.href);
    }
  }

  const className = clsx(
    styles['button'],
    styles[`button-${resolvedVariant}-${resolvedColor}`],
    isPlaceholder && styles['placeholder'],
    props.className,
  );

  const disabled = isPlaceholder || props.busy || props.disabled;
  const spinner = props.busy && (
    <ButtonSpinner position="absolute" size={size} />
  );

  return props.href && !disabled ? (
    <a
      ref={anchorMergedRef}
      className={className}
      href={props.href}
      onClick={onClickAnchor}
      style={style}
    >
      {props.children}
      {spinner}
    </a>
  ) : (
    <button
      ref={buttonMergedRef}
      aria-hidden={isPlaceholder}
      className={className}
      disabled={disabled}
      onClick={onClickButton}
      style={style}
      type={type}
    >
      {props.children}
      {spinner}
    </button>
  );
}
